import api from './api';

/**
 * Cross-module state + telemetry for the call-center softphone.
 *
 * Live-call flag — set by TelemarketingPage while a call is connecting or in
 * progress. The stale-chunk auto-reload path in main.tsx (`vite:preloadError`)
 * consults it so a deploy can never reload the tab under a live call: the
 * softphone (SIP.js over WSS) lives in this tab and a reload drops the call.
 * When a reload is suppressed we remember that a newer build is waiting so the
 * dialer can show a "refresh between calls" banner.
 *
 * reportDialerEvent() — posts softphone connection events (WS drop, SIP
 * unregister, reconnect attempts/outcomes, token-refresh failures, render
 * crashes) to POST /calls/client-event. Until now the only evidence of a
 * disconnect was console.log in the agent's browser; the server had no idea
 * how often it happens or why. Events that fail to send (the API is often
 * unreachable at the very moment the PBX socket drops) are queued and flushed
 * on the next report or when the browser comes back online.
 */

let liveCall = false;
let reloadPending = false;
const pendingListeners = new Set<(pending: boolean) => void>();

export function setLiveCall(v: boolean): void { liveCall = v; }
export function isLiveCall(): boolean { return liveCall; }

export function markReloadPending(): void {
  if (reloadPending) return;
  reloadPending = true;
  pendingListeners.forEach((cb) => { try { cb(true); } catch { /* listener bug — ignore */ } });
}
export function isReloadPending(): boolean { return reloadPending; }
export function onReloadPending(cb: (pending: boolean) => void): () => void {
  pendingListeners.add(cb);
  return () => { pendingListeners.delete(cb); };
}

export type DialerEvent =
  | 'pbx_disconnected'
  | 'pbx_reconnect_attempt'
  | 'pbx_reconnected'
  | 'pbx_reconnect_failed'
  | 'pbx_error'
  | 'token_refresh_failed'
  | 'chunk_reload_suppressed'
  | 'render_crash';

interface QueuedEvent { event: DialerEvent; details: Record<string, unknown>; at: string }

const MAX_QUEUE = 20;
const queue: QueuedEvent[] = [];
let flushing = false;

function envSnapshot(): Record<string, unknown> {
  return {
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
    path: typeof location !== 'undefined' ? location.pathname : undefined,
    liveCall,
  };
}

function hasSession(): boolean {
  try { return !!localStorage.getItem('token'); } catch { return false; }
}

export function reportDialerEvent(event: DialerEvent, details: Record<string, unknown> = {}): void {
  // Public pages (no session) have nothing to attribute the event to and the
  // endpoint is authenticated anyway — drop rather than trigger a 401 → login.
  if (!hasSession()) return;
  queue.push({ event, details: { ...envSnapshot(), ...details }, at: new Date().toISOString() });
  while (queue.length > MAX_QUEUE) queue.shift();
  void flush();
}

async function flush(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    while (queue.length) {
      const item = queue[0];
      try {
        await api.post('/calls/client-event', item, { timeout: 8000 });
        queue.shift();
      } catch {
        break; // API unreachable right now — keep queued, retried on the next report / online
      }
    }
  } finally {
    flushing = false;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { void flush(); });
}
